import { useQuery, useMutation } from "./urqlHooks";

// =============================================
// QUERIES
// =============================================

const CONTACT_ACCOUNT_SUGGESTIONS = `
  query ContactAccountSuggestions($companyId: ID!, $filter: ContactAccountSuggestionFilterInput!) {
    contactAccountSuggestions(companyId: $companyId, filter: $filter) {
      items {
        id
        agendaId
        contactRut
        contactName
        isReceived
        documentSubType
        suggestedAccountKey
        suggestedAccountName
        confidence
        source
        status
        materialityAmount
        documentsCount
        createdAt
        evidence {
          companiesCount
          companiesTop
          consensusRatio
          llmReason
          accountRemapped
          originalAccountKey
          rubroCluster
          rubroClusterLabel
          alternatives {
            accountKey
            accountName
            companiesCount
          }
        }
      }
      total
      page
      totalPages
    }
  }
`;

export const GetContactAccountSuggestions = (companyId, filter) => {
  const agendaIds = filter?.agendaIds;
  const pauseForEmptyAgendas = Array.isArray(agendaIds) && agendaIds.length === 0;
  const [result, reexecuteQuery] = useQuery({
    query: CONTACT_ACCOUNT_SUGGESTIONS,
    variables: { companyId, filter },
    pause: !companyId || pauseForEmptyAgendas,
  });
  return { ...result, reexecuteQuery };
};

const CONTACT_ACCOUNT_SUGGESTION_COUNTS = `
  query ContactAccountSuggestionCounts($companyId: ID!) {
    contactAccountSuggestionCounts(companyId: $companyId) {
      providers
      clients
      total
    }
  }
`;

export const GetContactAccountSuggestionCounts = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: CONTACT_ACCOUNT_SUGGESTION_COUNTS,
    variables: { companyId },
    pause: !companyId,
  });
  return { ...result, reexecuteQuery };
};

// =============================================
// MUTATIONS
// =============================================

const ACCEPT_CONTACT_ACCOUNT_SUGGESTION = `
  mutation AcceptContactAccountSuggestion($companyId: ID!, $input: AcceptContactAccountSuggestionInput!) {
    acceptContactAccountSuggestion(companyId: $companyId, input: $input) {
      status
      message
      affected
      createdRuleIds
    }
  }
`;

export const AcceptContactAccountSuggestion = () => {
  const [result, executeMutation] = useMutation(ACCEPT_CONTACT_ACCOUNT_SUGGESTION);
  return { ...result, executeMutation };
};

const REJECT_CONTACT_ACCOUNT_SUGGESTION = `
  mutation RejectContactAccountSuggestion($companyId: ID!, $input: RejectContactAccountSuggestionInput!) {
    rejectContactAccountSuggestion(companyId: $companyId, input: $input) {
      status
      message
      affected
    }
  }
`;

export const RejectContactAccountSuggestion = () => {
  const [result, executeMutation] = useMutation(REJECT_CONTACT_ACCOUNT_SUGGESTION);
  return { ...result, executeMutation };
};

const ACCEPT_CONTACT_ACCOUNT_SUGGESTIONS = `
  mutation AcceptContactAccountSuggestions($companyId: ID!, $input: BulkContactAccountSuggestionInput!) {
    acceptContactAccountSuggestions(companyId: $companyId, input: $input) {
      status
      message
      affected
      createdRuleIds
    }
  }
`;

export const AcceptContactAccountSuggestions = () => {
  const [result, executeMutation] = useMutation(ACCEPT_CONTACT_ACCOUNT_SUGGESTIONS);
  return { ...result, executeMutation };
};

const REJECT_CONTACT_ACCOUNT_SUGGESTIONS = `
  mutation RejectContactAccountSuggestions($companyId: ID!, $input: BulkContactAccountSuggestionInput!) {
    rejectContactAccountSuggestions(companyId: $companyId, input: $input) {
      status
      message
      affected
    }
  }
`;

export const RejectContactAccountSuggestions = () => {
  const [result, executeMutation] = useMutation(REJECT_CONTACT_ACCOUNT_SUGGESTIONS);
  return { ...result, executeMutation };
};

const GENERATE_CONTACT_ACCOUNT_SUGGESTIONS = `
  mutation GenerateContactAccountSuggestions($companyId: ID!) {
    generateContactAccountSuggestions(companyId: $companyId) {
      status
      message
      affected
    }
  }
`;

export const GenerateContactAccountSuggestions = () => {
  const [result, executeMutation] = useMutation(GENERATE_CONTACT_ACCOUNT_SUGGESTIONS);
  return { ...result, executeMutation };
};

const ACCOUNT_SUGGESTION_MANUAL_STATUS = `
  query AccountSuggestionManualStatus {
    accountSuggestionManualStatus {
      lastRebuildAt
      lastIncrementalAt
      patternsCount
      engineVersion
      lastRunDurationMs
    }
  }
`;

export const GetAccountSuggestionManualStatus = (options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: ACCOUNT_SUGGESTION_MANUAL_STATUS,
    pause: Boolean(options.pause),
  });
  return { ...result, reexecuteQuery };
};

const REFRESH_ACCOUNT_SUGGESTION_MANUAL = `
  mutation RefreshAccountSuggestionManual {
    refreshAccountSuggestionManual {
      status
      message
      patternsUpserted
      rutsProcessed
      patternsCount
      durationMs
      lastRebuildAt
    }
  }
`;

export const RefreshAccountSuggestionManual = () => {
  const [result, executeMutation] = useMutation(REFRESH_ACCOUNT_SUGGESTION_MANUAL);
  return { ...result, executeMutation };
};
